import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LuMic, LuSparkles, LuSquare } from "react-icons/lu";
import Select, {
  components,
  GroupBase,
  MenuListProps,
  MultiValue,
  OptionProps,
  SelectInstance,
} from "react-select";

import {
  ASK_TIP,
  canAsk,
  looksLikeQuestion,
} from "../../features/ai/ask/ask.utils";
import {
  appendDictation,
  useDictation,
} from "../../features/work/pages/details/widgets/work-notes/useDictation";
import { SelectOptionType } from "../../types/types";
import { AvatarPlaceholder } from "../avatar/AvatarPlaceholder";
import { LottieLoading } from "../graphics/LottieLoading";

interface Props {
  options: SelectOptionType[];
  placeholder?: string;
  noOptionsMessage?: string;
  onChange: (newValue: MultiValue<SelectOptionType> | SelectOptionType) => void;
  setIsOpenAddContact?: (value: boolean, typedText?: string) => void;
  value: SelectOptionType[] | SelectOptionType | null;
  inputValue?: string;
  onChangeInput?: (inputValue: string) => void;
  isLoading: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  workTable?: boolean;
  removeOptions?: boolean;
  /**
   * "Ask ProCharted" (header search, only when AI is on): the first row
   * offers to ask the typed text; picked = `onAsk`.
   */
  aiAsk?: { onAsk: (question: string) => void };
}

const ASK_VALUE = "__ai_ask__";
const NO_RESULTS_VALUE = "__no_results__";

const AskRow: FC<{ text: string; isFocused: boolean }> = ({
  text,
  isFocused,
}) => (
  <div
    className={classNames(
      "-mx-3 -my-2 flex min-w-0 items-center gap-2 px-3 py-2.5 font-Manrope",
      isFocused ? "bg-[#EEEAFF]" : "bg-white",
    )}>
    <span
      aria-hidden={"true"}
      className={
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EEEAFF] text-[#6B4EFF]"
      }>
      <LuSparkles size={14} strokeWidth={2.3} />
    </span>
    <span className={"min-w-0 truncate text-[14px] text-[#1F2433]"}>
      <span className={"font-bold text-[#6B4EFF]"}>Ask ProCharted: </span>
      <span className={"font-semibold"}>“{text}”</span>
    </span>
    {isFocused && (
      <span
        className={
          "ml-auto shrink-0 rounded-[6px] border border-[#D9D1FF] bg-white px-1.5 text-[11px] font-bold text-[#6B4EFF]"
        }>
        Enter
      </span>
    )}
  </div>
);

const InputOption = ({
  getStyles,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  data,
  ...rest
}: OptionProps<SelectOptionType, true, GroupBase<SelectOptionType>>) => {
  const [isActive, setIsActive] = useState(false);
  const onMouseDown = () => setIsActive(true);
  const onMouseUp = () => setIsActive(false);
  const onMouseLeave = () => setIsActive(false);

  if (data?.isGroupLabel) {
    return (
      <components.Option
        {...rest}
        isDisabled={isDisabled}
        isFocused={false}
        isSelected={isSelected}
        getStyles={getStyles}
        data={data}
        innerProps={{
          ...innerProps,
          style: { backgroundColor: "transparent", cursor: "default" },
        }}>
        <div
          className={
            "pt-1 font-Manrope text-[11px] font-bold uppercase tracking-wide text-[#6B7185]"
          }>
          {data.label}
        </div>
      </components.Option>
    );
  }

  if (data?.isAiAsk || data?.value === NO_RESULTS_VALUE) {
    return (
      <components.Option
        {...rest}
        isDisabled={isDisabled}
        isFocused={isFocused}
        isSelected={isSelected}
        getStyles={getStyles}
        data={data}
        innerProps={{
          ...innerProps,
          style: {
            backgroundColor: "transparent",
            overflow: "hidden",
            cursor: data?.isAiAsk ? "pointer" : "default",
            borderBottom: data?.isAiAsk ? "1px solid #EDEFF3" : undefined,
          },
        }}>
        {data?.isAiAsk ? (
          <AskRow text={data.label} isFocused={isFocused} />
        ) : (
          <div className={"!font-notoSans text-sm text-gray-500"}>
            {data?.label}
          </div>
        )}
      </components.Option>
    );
  }

  // styles
  let bg = "transparent";
  if (isFocused) bg = "#eee";
  if (isActive) bg = "#B2D4FF";

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex ",
    fontWeight: "500",
    fontSize: "14px",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    overflow: "hidden",
  };

  // prop assignment
  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style,
  };

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      data={data}
      innerProps={props}>
      <div
        className={classNames(
          "grid !font-notoSans",
          data?.caption && "grid-rows-2",
          data?.avatar && "grid-cols-[24px_1fr] gap-2",
        )}>
        {data?.avatar && (
          <div
            className={classNames(
              "mr-2 font-normal",
              data?.caption && "row-span-2 self-center",
            )}>
            <AvatarPlaceholder
              size={"2xs"}
              label={data?.avatar}
              type={data?.avatarColor ? data?.avatarColor : "blue"}
            />
          </div>
        )}
        <div
          className={classNames(
            "truncate !font-notoSans",
            data?.isBold && "font-bold",
          )}>
          {children}
        </div>
        {data?.caption && (
          <div
            className={classNames(
              "truncate !font-notoSans text-xs text-gray-500",
              data?.avatar && "col-start-2",
            )}>
            {data?.caption}
          </div>
        )}
      </div>
    </components.Option>
  );
};

type MenuListPropsType = MenuListProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
> & {
  setIsOpenAddContact?: (value: boolean, typedText?: string) => void;
  isLoading?: boolean;
};

const MenuList = ({
  children,
  setIsOpenAddContact,
  isLoading,
  ...props
}: MenuListPropsType) => {
  // "Ask ProCharted" stays the very first row (above "Create new contact").
  const items = React.Children.toArray(children);
  const askFirst = !!(props.options[0] as SelectOptionType | undefined)
    ?.isAiAsk;
  const askRow = askFirst ? items[0] : null;
  const rest = askFirst ? items.slice(1) : children;
  return (
    <components.MenuList {...props}>
      {isLoading ? (
        <LottieLoading />
      ) : (
        <>
          {askRow}
          {setIsOpenAddContact && (
            <div
              className={
                "flex cursor-pointer items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-400 hover:text-white"
              }
              onClick={() => {
                if (setIsOpenAddContact) {
                  // Read the typed text first: closing the menu may clear it.
                  const typedText = props.selectProps.inputValue || "";
                  props.selectProps.onMenuClose();
                  setIsOpenAddContact(true, typedText);
                }
              }}>
              <div
                className={
                  "flex h-4 w-4 flex-nowrap items-center justify-center rounded-full border-[1px] border-blue-600"
                }>
                <FontAwesomeIcon
                  icon={faPlus}
                  className={"text-[10px] text-blue-600"}
                />
              </div>
              <div className={"whitespace-nowrap font-bold"}>
                Create new contact
              </div>
            </div>
          )}
          {rest}
        </>
      )}
    </components.MenuList>
  );
};

const Search: FC<Props> = ({
  options,
  onChange,
  placeholder,
  value,
  inputValue,
  onChangeInput,
  isLoading,
  noOptionsMessage,
  onFocus,
  onBlur,
  setIsOpenAddContact,
  removeOptions,
  aiAsk,
}) => {
  const dropDownRef = useRef<HTMLDivElement>(null);
  const selectRef =
    useRef<SelectInstance<SelectOptionType, true, GroupBase<SelectOptionType>>>(
      null,
    );

  const askText = (inputValue || "").trim();
  const showAsk = !!aiAsk && canAsk(askText);
  const isQuestion = showAsk && looksLikeQuestion(askText);

  const allOptions: SelectOptionType[] = useMemo(() => {
    if (!showAsk) return options;
    const ask: SelectOptionType = {
      value: ASK_VALUE,
      label: askText,
      isAiAsk: true,
    };
    const empty: SelectOptionType[] =
      !isLoading && options.length === 0
        ? [
            {
              value: NO_RESULTS_VALUE,
              label: noOptionsMessage || "No results",
              isDisabled: true,
            },
          ]
        : [];
    return [ask, ...options, ...empty];
  }, [showAsk, askText, options, isLoading, noOptionsMessage]);

  // The Ask row is first. It is pre-selected (Enter asks) only for
  // question-like text; otherwise Enter keeps opening the first result.
  useEffect(() => {
    const select = selectRef.current;
    if (!select || !showAsk) return;
    const target = isQuestion
      ? allOptions[0]
      : allOptions.find((option) => !option.isAiAsk && !option.isDisabled);
    if (!target || select.state.focusedOption === target) return;
    try {
      select.setState({
        focusedOption: target,
        focusedOptionId: select.getFocusedOptionId(target),
      });
    } catch {
      // Keyboard focus is only a convenience.
    }
  }, [allOptions, showAsk, isQuestion]);

  const handleChange = (
    item: MultiValue<SelectOptionType> | SelectOptionType | null,
  ) => {
    const option = item as SelectOptionType | null;
    if (option?.isAiAsk) {
      aiAsk?.onAsk(option.label);
      selectRef.current?.blur();
      return;
    }
    if (option?.value === NO_RESULTS_VALUE || !item) return;
    onChange(item);
  };

  // Microphone (Roadmap 1836): the same browser dictation as the work notes
  // chat; spoken words are added to the search text and the results open.
  const inputRef = useRef(inputValue || "");
  inputRef.current = inputValue || "";
  const dictation = useDictation((spoken) => {
    onChangeInput?.(appendDictation(inputRef.current, spoken));
    selectRef.current?.focus();
    selectRef.current?.openMenu("first");
  });

  const handleFocus = useCallback((e) => onFocus && onFocus(e), [onFocus]);
  const handleBlur = useCallback((e) => onBlur && onBlur(e), [onBlur]);

  const selectStyles = useMemo(
    () => ({
      indicatorsContainer: () => ({ display: "none" }),
      menuPortal: (base: any) =>
        removeOptions
          ? { display: "none" }
          : {
              ...base,
              left: dropDownRef.current?.getBoundingClientRect().left,
              zIndex: 9999,
              width: dropDownRef.current?.getBoundingClientRect().width,
              marginLeft: 0,
              display: inputValue ? "block" : "none",
            },
      placeholder: (base: any) => ({
        ...base,
        // Same size as the "Ask ProCharted: …" row (Roadmap 1835).
        fontSize: "14px",
        // One line, cut with "…" if it doesn't fit (QA batch 11, phones).
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontFamily:
          "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif !important",
      }),
      singleValue: (base: any) => ({ ...base, fontSize: 14, fontWeight: 500 }),
      valueContainer: (base: any) => ({
        ...base,
        flexWrap: "nowrap",
        padding: 0,
      }),
      input: (base: any) => ({
        ...base,
        fontSize: "14px",
        color: "#1F2433",
        margin: 0,
        cursor: "text",
        width: "100%",
      }),
      control: (base: any) => ({
        ...base,
        minHeight: "100%",
        padding: "0",
        width: "100%",
        boxShadow: "none",
        borderRadius: "0",
        border: "none",
      }),
      container: (base: any) => ({ ...base, width: "100%" }),
    }),
    [removeOptions, inputValue],
  );

  return (
    <div
      ref={dropDownRef}
      className={
        "relative flex h-full w-full items-center gap-2 rounded-[8px] bg-white px-[10px]"
      }>
      <Select
        ref={selectRef}
        value={value}
        placeholder={placeholder}
        filterOption={null}
        hideSelectedOptions={false}
        onChange={handleChange}
        inputValue={inputValue}
        noOptionsMessage={() => noOptionsMessage || "No results"}
        onInputChange={(newValue) => onChangeInput && onChangeInput(newValue)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isLoading={isLoading}
        menuPortalTarget={document.body}
        styles={selectStyles}
        components={{
          Option: InputOption,
          MenuList: ({ children, ...props }) => (
            <MenuList {...props} setIsOpenAddContact={setIsOpenAddContact}>
              {children}
            </MenuList>
          ),
        }}
        options={allOptions}
      />
      {dictation.supported && onChangeInput && (
        <button
          type={"button"}
          aria-label={
            dictation.isListening ? "Stop dictation" : "Search by voice"
          }
          title={
            dictation.error ||
            (dictation.isListening
              ? "Listening… press again to stop"
              : "Speak instead of typing")
          }
          // Keeps the search box focused, so the typed text isn't cleared.
          onMouseDown={(event) => event.preventDefault()}
          onClick={dictation.isListening ? dictation.stop : dictation.start}
          className={classNames(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
            dictation.isListening
              ? "animate-pulse bg-[#FDECEC] text-[#E5484D]"
              : dictation.error
              ? "text-[#E5484D] hover:bg-[#FDECEC]"
              : "text-gray-500 hover:bg-[#F4F2FE] hover:text-[#6B4EFF]",
          )}>
          {dictation.isListening ? (
            <LuSquare size={11} fill={"currentColor"} />
          ) : (
            <LuMic size={16} strokeWidth={2.2} />
          )}
        </button>
      )}
      {aiAsk && (
        <div className={"group relative flex shrink-0"}>
          <button
            type={"button"}
            aria-label={ASK_TIP}
            onClick={() => selectRef.current?.focus()}
            className={
              "flex h-7 w-7 items-center justify-center rounded-full text-[#6B4EFF] hover:bg-[#EEEAFF]"
            }>
            <LuSparkles size={16} strokeWidth={2.2} />
          </button>
          <div
            role={"tooltip"}
            className={
              "pointer-events-none invisible absolute left-1/2 top-full z-[1] mt-2 w-64 -translate-x-1/2 rounded-[10px] bg-[#1F2433] px-3 py-2 font-Manrope text-[12px] font-medium leading-[1.45] text-white opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100"
            }>
            {ASK_TIP}
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
